import type { Connection, Edge } from '@xyflow/react';
import { makeAutoObservable, runInAction } from 'mobx';
import type { WorkflowActionCreateDTO, WorkflowCreateDTO } from '@repositories/workflowRepository/workflow.types';
import type { WorkflowActionDTOModel } from '@repositories/workflowRepository/models/WorkflowActionDTOModel';
import type { WorkflowExecModel } from '@repositories/workflowRepository/models/WorkflowExecModel';
import type { WorkflowModel } from '@repositories/workflowRepository/models/WorkflowModel';
import type { WorkflowTriggerDTOModel } from '@repositories/workflowRepository/models/WorkflowTriggerDTOModel';
import {
  generateActionNode,
  generateConditionNode,
  generateConditionThenEdge,
  generateEndNode,
  generateExceptionEdge,
  generateGraph,
  generateNormalEdge,
  getLayoutedElements,
  traverseGraph,
  type WorkflowNode,
} from './graph/GraphGenerator';

export type ToolbarView =
  | ''
  | 'workflowInfo' // 워크플로우 정보 화면
  | 'triggerSelect' // 트리거 선택 화면
  | 'triggerConfig' // 트리거 설정 화면
  | 'triggerExec' // 트리거 실행 화면
  | 'actionSelect' // 액션 선택 화면
  | 'actionConfig' // 액션 설정 화면
  | 'actionExec' // 액션 실행 화면
  | 'conditionSelect' // 조건 선택 화면
  | 'conditionConfig' // 조건 설정 화면
  | 'conditionExec'; // 조건 실행 화면

class WorkflowDetailStore {
  private _isEdit: boolean; // 편집 모드 여부
  private _toolbar: ToolbarView; // 사이드바 화면 상태
  private readonly _createDTO: WorkflowCreateDTO; // 워크플로우 생성 모델
  private _nodes: WorkflowNode[]; // React Flow 노드 배열
  private readonly _nodeMap: Map<string, WorkflowNode>;
  private _edges: Edge[]; // React Flow 엣지 배열
  private _curNodeId?: string; // 현재 선택된 노드 ID
  private _trigger?: WorkflowTriggerDTOModel; // 트리거 모델
  private _actions: WorkflowActionDTOModel[]; // 액션 배열
  private _lastActionNodeIndex: number; // 마지막 액션 인덱스
  private _lastConditionNodeIndex: number; // 마지막 조건 노드 인덱스
  private _lastEndNodeIndex: number; // 마지막 End 노드 인덱스
  private _execution?: WorkflowExecModel; // 실행 모델
  reactFlowContainerRef: HTMLDivElement | null = null; // ReactFlow 컨테이너 ref (observable 제외)

  constructor() {
    this._isEdit = false;
    this._createDTO = {
      actions: [],
      name: '',
      trigger: {
        baseTriggerId: '',
      },
      type: 'CUSTOM',
      description: undefined,
      id: undefined,
      tenantId: '',
    };
    this._toolbar = '';
    this._nodes = [];
    this._nodeMap = new Map<string, WorkflowNode>();
    this._edges = [];
    this._curNodeId = undefined;
    this._trigger = undefined;
    this._actions = [];
    this._lastActionNodeIndex = 0;
    this._lastConditionNodeIndex = 0;
    this._lastEndNodeIndex = 0;
    this._execution = undefined;

    makeAutoObservable(this, {
      reactFlowContainerRef: false, // ref는 observable에서 제외
    });
  }

  get isEdit() {
    return this._isEdit;
  }

  setIsEdit(isEdit: boolean) {
    this._isEdit = isEdit;
  }

  setTitle(title: string) {
    this._createDTO.name = title;
  }

  get title() {
    return this._createDTO.name;
  }

  get toolbar() {
    return this._toolbar;
  }

  setToolbarView(type: ToolbarView) {
    runInAction(() => {
      this._toolbar = type;
    });
  }

  get curNodeId(): string | undefined {
    return this._curNodeId;
  }

  set curNodeId(nodeId: string) {
    this._curNodeId = nodeId;
  }

  get nodes() {
    return this._nodes ?? [];
  }

  set nodes(nodes: WorkflowNode[]) {
    this._nodes = nodes;

    this._nodeMap.clear();
    this.nodes.forEach(node => {
      this._nodeMap.set(node.id, node);
    });
  }

  get edges() {
    return this._edges ?? [];
  }

  set edges(edges: Edge[]) {
    this._edges = edges;
  }

  get triggerNode() {
    return this.nodes.find(node => node.data.type === 'trigger');
  }

  get actionNodes() {
    return this.nodes.filter(node => node.data.type === 'action');
  }

  get execution(): WorkflowExecModel | undefined {
    return this._execution;
  }

  findNode(nodeId: string) {
    return this._nodeMap.get(nodeId);
  }

  // 노드의 실제 step 번호를 계산 (서버의 executedActionByStep 인덱스와 매칭)
  getNodeStep(nodeId: string): number {
    const sortedNodes = [...this._nodes]
      .filter(n => n.type === 'action' || n.type === 'condition')
      .sort((a, b) => {
        const aIndex = a.data.actionIndex ?? a.data.conditionIndex ?? 0;
        const bIndex = b.data.actionIndex ?? b.data.conditionIndex ?? 0;
        return aIndex - bIndex;
      });

    return sortedNodes.findIndex(n => n.id === nodeId);
  }

  findEdgeBySource(souce: string, type?: string) {
    return this.edges.find(edge => edge.source === souce && (type ? type === edge.type : true));
  }

  // NOTE. 생성 모드인지 체크
  checkEditable() {
    const isCreate = window.location.pathname.includes('create');
    return !(!isCreate && !this._isEdit);
  }

  reconstruct(nodes: WorkflowNode[], edges: Edge[]) {
    runInAction(() => {
      let actionStep = 0; // action과 condition이 공유하는 step (서버 전송용)
      let actionIndex = 0; // action 노드의 표시 인덱스 (화면 표시용)
      let conditionIndex = 0; // condition 노드의 표시 인덱스 (화면 표시용)
      const newNodes: WorkflowNode[] = [];

      // 1. 노드 Map 생성
      const nodeMap = new Map(nodes.map(node => [node.id, node]));
      const nodeIds = new Set(nodes.map(n => n.id));

      // 2. 유효한 엣지만 필터링 (존재하는 노드를 참조하는 엣지만)
      const preFilteredEdges = edges.filter(edge => nodeIds.has(edge.source) && nodeIds.has(edge.target));

      // 3. 그래프 순회하며 각 노드의 인덱스와 연결 정보 업데이트
      traverseGraph(nodes, preFilteredEdges, (node, nodeInfo) => {
        const updatedNode = node;
        const { incomingEdges, outgoingEdges } = nodeInfo;

        if (updatedNode.type === 'action') {
          // 3. 액션 표시 인덱스 부여
          updatedNode.data.actionIndex = actionIndex;

          // 4. ACTION 노드의 엣지가 없으면 해당 스텝 처리(-1)
          if (updatedNode.data.actionModel) {
            // 5. nextStep, conditionThenStep, exceptionCatchStep 계산
            if (outgoingEdges.filter(edge => edge.type === 'normal').length === 0) {
              updatedNode.data.actionModel.nextStep = -1;
            }
            if (outgoingEdges.filter(edge => edge.type === 'conditionThen').length === 0) {
              updatedNode.data.actionModel.conditionThenStep = -1;
            }
            if (outgoingEdges.filter(edge => edge.type === 'exception').length === 0) {
              updatedNode.data.actionModel.exceptionCatchStep = -1;
            }
          }

          // 6. ACTION 노드로 들어오는 모든 스텝 번호 수정
          incomingEdges.forEach(edge => {
            const sourceNode = nodeMap.get(edge.source);
            if (!sourceNode?.data.actionModel) return;

            switch (edge.type) {
              case 'conditionThen':
                sourceNode.data.actionModel.conditionThenStep = actionStep;
                break;
              case 'exception':
                sourceNode.data.actionModel.exceptionCatchStep = actionStep;
                break;
              case 'normal':
              default:
                sourceNode.data.actionModel.nextStep = actionStep;
            }
          });

          actionIndex += 1;
          actionStep += 1;
        }

        if (updatedNode.type === 'condition') {
          // 7. 조건 표시 인덱스 부여
          updatedNode.data.conditionIndex = conditionIndex;

          // 8. CONDITION 노드의 엣지가 없으면 해당 스텝 처리(-1)
          if (updatedNode.data.actionModel) {
            // 9. CONDITION 노드의 엣지가 없으면 해당 스텝 처리(-1)
            if (outgoingEdges.filter(edge => edge.type === 'normal').length === 0) {
              updatedNode.data.actionModel.nextStep = -1;
            }
          }

          // 10. CONDITION 노드로 들어오는 모든 스텝 번호 수정
          incomingEdges.forEach(edge => {
            const sourceNode = nodeMap.get(edge.source);
            if (!sourceNode?.data.actionModel) return;

            switch (edge.type) {
              case 'conditionThen':
                sourceNode.data.actionModel.conditionThenStep = actionStep;
                break;
              case 'normal':
              default:
                sourceNode.data.actionModel.nextStep = actionStep;
            }
          });

          conditionIndex += 1;
          actionStep += 1;
        }

        // 11. END 노드로 들어오는 모든 스텝 처리(-1)
        if (updatedNode.type === 'end') {
          incomingEdges.forEach(edge => {
            const sourceNode = nodeMap.get(edge.source);
            if (!sourceNode?.data.actionModel) return;
            sourceNode.data.actionModel.nextStep = -1;
          });
        }

        newNodes.push(updatedNode);
      });

      // 12. 실제로 그래프에 포함된 노드의 ID만 수집 (traverseGraph에서 도달 가능한 노드만)
      const reachableNodeIds = new Set(newNodes.map(n => n.id));

      // 13. 도달 가능한 노드만 참조하는 엣지 필터링
      const validEdges = preFilteredEdges.filter(
        edge => reachableNodeIds.has(edge.source) && reachableNodeIds.has(edge.target),
      );

      // 14. Dagre로 레이아웃 재계산
      const result = getLayoutedElements(newNodes, validEdges);

      // 15. 최종 검증: 레이아웃 결과에서 불필요한 엣지 제거
      const finalNodeIds = new Set(result.nodes.map(n => n.id));
      const finalEdges = result.edges.filter(edge => finalNodeIds.has(edge.source) && finalNodeIds.has(edge.target));

      this.nodes = result.nodes;
      this.edges = finalEdges;
    });
  }

  removeActionNode(nodeId: string) {
    const incomingEdges = this.edges.filter(edge => edge.target === nodeId);
    const outgoingEdges = this.edges.filter(edge => edge.source === nodeId);

    const newEdges: Edge[] = [];

    incomingEdges.forEach(incomingEdge => {
      // 분기 엣지(exception/conditionThen)로 들어오는 경우는 복구하지 않음
      if (incomingEdge.type === 'exception' || incomingEdge.type === 'conditionThen') return;

      // normal incoming edge만 처리
      // 나가는 엣지 중 normal 엣지만 연결 (분기 엣지 타겟은 사라져야 함)
      const normalOutgoingEdge = outgoingEdges.find(edge => edge.type === 'normal');
      if (normalOutgoingEdge) {
        const newEdge: Edge = generateNormalEdge(incomingEdge.source, normalOutgoingEdge.target);
        newEdges.push(newEdge);
      }
    });

    // 삭제할 노드와 관련된 모든 엣지 제거
    const updatedEdges = this.edges.filter(edge => edge.source !== nodeId && edge.target !== nodeId);
    const newNodes = this.nodes.filter(node => node.id !== nodeId);

    // 노드 재구성 (불필요한 노드/엣지는 reconstruct에서 정리됨)
    this.reconstruct(newNodes, [...updatedEdges, ...newEdges]);
  }

  /**
   * 액션 노드 추가
   * @param prevNodeId 이전 노드 ID
   * @param model 액션 모델
   * @param targetNodeId 타겟 노드 ID (선택)
   * @returns 새로운 액션 노드
   */
  addActionNode(prevNodeId: string, model?: WorkflowActionDTOModel, targetNodeId?: string): WorkflowNode {
    // 1. 새로운 노드의 고유 ID 생성
    const newActionIndex = this._lastActionNodeIndex;

    // 2. 새로운 액션 노드 생성
    const newActionNode = generateActionNode(newActionIndex, model);

    // 3. 새로운 엣지 생성
    const newEdges: Edge[] = [];

    if (targetNodeId) {
      // 4. 특정 엣지 사이에 삽입
      const updatedEdges = this.edges.filter(
        edge => !(edge.source === prevNodeId && edge.target === targetNodeId && edge.type === 'normal'),
      );

      const edgeToNewNode = generateNormalEdge(prevNodeId, newActionNode.id);
      const edgeFromNewNode = generateNormalEdge(newActionNode.id, targetNodeId);
      newEdges.push(edgeToNewNode, edgeFromNewNode);

      this.reconstruct([...this.nodes, newActionNode], [...newEdges, ...updatedEdges]);
    } else {
      // 기존 로직: 모든 normal outgoing edges 재연결
      const outgoingEdges = this.edges.filter(edge => edge.source === prevNodeId && edge.type === 'normal');
      const updatedEdges = this.edges.filter(edge => !(edge.source === prevNodeId && edge.type === 'normal'));

      const newEdgeToNewNode: Edge = generateNormalEdge(prevNodeId, newActionNode.id);
      newEdges.push(newEdgeToNewNode);

      const reconnectedEdges = outgoingEdges.map(edge => generateNormalEdge(newActionNode.id, edge.target));
      newEdges.push(...reconnectedEdges);

      this.reconstruct([...this.nodes, newActionNode], [...newEdges, ...updatedEdges]);
    }

    this._lastActionNodeIndex += 1;

    return newActionNode;
  }

  addExceptionActionNode(prevNodeId: string, model?: WorkflowActionDTOModel): WorkflowNode {
    // 새로운 노드의 고유 ID 생성
    const newActionIndex = this._lastActionNodeIndex;
    const newEndIndex = this._lastEndNodeIndex;

    // 새로운 액션 노드 생성
    const newActionNode = generateActionNode(newActionIndex, model);
    if (newActionNode.data.actionModel) {
      newActionNode.data.actionModel.nextStep = -1;
    }
    const newExceptionEdge = generateExceptionEdge(prevNodeId, newActionNode.id);
    const newEndNode = generateEndNode(newEndIndex);
    const newNormalEdge = generateNormalEdge(newActionNode.id, newEndNode.id);

    // 노드 재구성
    this.reconstruct([...this.nodes, newActionNode, newEndNode], [...this.edges, newNormalEdge, newExceptionEdge]);

    this._lastActionNodeIndex += 1;
    this._lastEndNodeIndex += 1;

    return newActionNode;
  }

  /**
   * Exception 경로에 조건 노드 추가
   * @param prevNodeId 이전 노드 ID
   * @param model 조건 액션 모델 (IF_ACTION)
   * @returns 새로운 조건 노드
   */
  addExceptionConditionNode(prevNodeId: string, model?: WorkflowActionDTOModel): WorkflowNode {
    // 새로운 노드의 고유 ID 생성
    const newConditionIndex = this._lastConditionNodeIndex;
    const newEndIndex = this._lastEndNodeIndex;

    // 새로운 조건 노드 생성
    const newConditionNode = generateConditionNode(newConditionIndex, model);
    if (newConditionNode.data.actionModel) {
      newConditionNode.data.actionModel.nextStep = -1;
    }
    const newExceptionEdge = generateExceptionEdge(prevNodeId, newConditionNode.id);
    const newEndNode = generateEndNode(newEndIndex);
    const newNormalEdge = generateNormalEdge(newConditionNode.id, newEndNode.id);

    // 노드 재구성
    this.reconstruct([...this.nodes, newConditionNode, newEndNode], [...this.edges, newNormalEdge, newExceptionEdge]);

    this._lastConditionNodeIndex += 1;
    this._lastEndNodeIndex += 1;

    return newConditionNode;
  }

  /**
   * 조건 노드 추가
   * @param prevNodeId 이전 노드 ID
   * @param model 조건 액션 모델 (IF_ACTION)
   * @param targetNodeId 타겟 노드 ID (선택)
   * @returns 새로운 조건 노드
   */
  addConditionNode(prevNodeId: string, model?: WorkflowActionDTOModel, targetNodeId?: string): WorkflowNode {
    // 새로운 노드의 고유 ID 생성
    const newConditionIndex = this._lastConditionNodeIndex;

    // 새로운 조건 노드 생성
    const newConditionNode = generateConditionNode(newConditionIndex, model);

    const newEdges: Edge[] = [];

    if (targetNodeId) {
      // 특정 엣지 사이에 삽입
      const updatedEdges = this.edges.filter(
        edge => !(edge.source === prevNodeId && edge.target === targetNodeId && edge.type === 'normal'),
      );

      const edgeToNewNode = generateNormalEdge(prevNodeId, newConditionNode.id);
      const edgeFromNewNode = generateNormalEdge(newConditionNode.id, targetNodeId);
      newEdges.push(edgeToNewNode, edgeFromNewNode);

      this.reconstruct([...this.nodes, newConditionNode], [...newEdges, ...updatedEdges]);
    } else {
      // 기존 로직: 모든 normal outgoing edges 재연결
      const outgoingEdges = this.edges.filter(edge => edge.source === prevNodeId && edge.type === 'normal');
      const updatedEdges = this.edges.filter(edge => !(edge.source === prevNodeId && edge.type === 'normal'));

      const newEdgeToNewNode: Edge = generateNormalEdge(prevNodeId, newConditionNode.id);
      newEdges.push(newEdgeToNewNode);

      const reconnectedEdges = outgoingEdges.map(edge => generateNormalEdge(newConditionNode.id, edge.target));
      newEdges.push(...reconnectedEdges);

      this.reconstruct([...this.nodes, newConditionNode], [...newEdges, ...updatedEdges]);
    }

    this._lastConditionNodeIndex += 1;

    return newConditionNode;
  }

  addConditionThenActionNode(prevNodeId: string, model?: WorkflowActionDTOModel): WorkflowNode {
    // 새로운 노드의 고유 ID 생성
    const newActionIndex = this._lastActionNodeIndex;
    const newEndIndex = this._lastEndNodeIndex;

    // 새로운 액션 노드 생성
    const newActionNode = generateActionNode(newActionIndex, model);
    if (newActionNode.data.actionModel) {
      newActionNode.data.actionModel.nextStep = -1;
    }
    const newCondtionThenEdge = generateConditionThenEdge(prevNodeId, newActionNode.id);
    const newEndNode = generateEndNode(newEndIndex);
    const newNormalEdge = generateNormalEdge(newActionNode.id, newEndNode.id);

    // 노드 재구성
    this.reconstruct([...this.nodes, newActionNode, newEndNode], [...this.edges, newNormalEdge, newCondtionThenEdge]);

    this._lastActionNodeIndex += 1;
    this._lastEndNodeIndex += 1;

    return newActionNode;
  }

  addConditionThenConditionNode(prevNodeId: string, model?: WorkflowActionDTOModel): WorkflowNode {
    // 새로운 노드의 고유 ID 생성
    const newConditionIndex = this._lastConditionNodeIndex;
    const newEndIndex = this._lastEndNodeIndex;

    // 새로운 조건 노드 생성
    const newConditionNode = generateConditionNode(newConditionIndex, model);
    if (newConditionNode.data.actionModel) {
      newConditionNode.data.actionModel.nextStep = -1;
    }

    // true 브랜치: 새로운 조건 노드로 연결
    const newConditionThenEdge = generateConditionThenEdge(prevNodeId, newConditionNode.id);
    const newEndNode = generateEndNode(newEndIndex);
    const newNormalEdge = generateNormalEdge(newConditionNode.id, newEndNode.id);

    // 노드 재구성
    this.reconstruct(
      [...this.nodes, newConditionNode, newEndNode],
      [...this.edges, newNormalEdge, newConditionThenEdge],
    );

    this._lastConditionNodeIndex += 1;
    this._lastEndNodeIndex += 1;

    return newConditionNode;
  }

  initNodes(isEditMode: boolean, model?: WorkflowModel, execModel?: WorkflowExecModel) {
    // 1. 원본 데이터 저장
    this._trigger = model?.trigger;
    this._actions = model?.actions ? [...model.actions] : [];
    this._execution = execModel;
    // 2. GraphGenerator로 노드와 엣지 생성
    const initialElements = generateGraph({ trigger: this._trigger, actions: this._actions });
    // 3. 상태에 반영
    this.nodes = initialElements.nodes;
    this.edges = initialElements.edges;
    // 4. 마지막 인덱스 초기화 (새 노드 ID 생성용)
    this._lastActionNodeIndex =
      this._actions.filter(action => action.type !== 'IF_ACTION' && action.type !== 'END_ACTION').length;
    this._lastConditionNodeIndex = this._actions.filter(action => action.type === 'IF_ACTION').length;
    // End 노드 수는 생성된 노드에서 계산
    this._lastEndNodeIndex = initialElements.nodes.filter(node => node.type === 'end').length;
  }

  /**
   * 여러 노드의 위치를 한 번에 업데이트
   * @param updates 노드 ID와 위치 배열
   */
  updateNodesPosition(updates: Array<{ id: string; position: { x: number; y: number } }>) {
    runInAction(() => {
      updates.forEach(({ id, position }) => {
        const nodeIndex = this._nodes.findIndex(node => node.id === id);
        if (nodeIndex !== -1) {
          this._nodes[nodeIndex] = {
            ...this._nodes[nodeIndex],
            position,
          };
          this._nodeMap.set(id, this._nodes[nodeIndex]);
        }
      });
    });
  }

  /**
   * 노드 연결 결과 타입
   */
  connectNodes(connection: Connection): { success: boolean; errorKey?: string } {
    const { source: sourceNodeId, target: targetNodeId, sourceHandle: edgeType } = connection;
    if (!sourceNodeId || !targetNodeId) return { success: false };

    // NOTE. 연결은 일반 엣지만 가능!!!
    if (edgeType !== 'normal') return { success: false };

    // 소스/타겟 노드가 존재하는지 확인
    const sourceNode = this.nodes.find(n => n.id === sourceNodeId);
    const targetNode = this.nodes.find(n => n.id === targetNodeId);
    if (!sourceNode || !targetNode) return { success: false };

    // 자기 자신에게 연결 방지
    if (sourceNodeId === targetNodeId) return { success: false };

    // 🚫 논리적으로 맞지 않는 연결 방지:
    // 같은 소스에서 이미 exception으로 연결된 타겟에 normal 연결 금지
    const hasExceptionEdgeToTarget = this.edges.some(
      edge => edge.source === sourceNodeId && edge.target === targetNodeId && edge.type === 'exception',
    );
    if (hasExceptionEdgeToTarget) {
      return { success: false, errorKey: 'Workflow_Connect_Error_Exception' };
    }

    // 같은 소스에서 이미 conditionThen으로 연결된 타겟에 normal 연결 금지
    const hasConditionThenEdgeToTarget = this.edges.some(
      edge => edge.source === sourceNodeId && edge.target === targetNodeId && edge.type === 'conditionThen',
    );
    if (hasConditionThenEdgeToTarget) {
      return { success: false, errorKey: 'Workflow_Connect_Error_ConditionThen' };
    }

    const existingEdge = this.edges.find(
      edge => edge.source === sourceNodeId && edge.target === targetNodeId && edge.type === edgeType,
    );

    if (existingEdge) return { success: false };

    // 기존의 동일 유형(normal)의 엣지만 제거 (exception, conditionThen은 유지)
    const updatedEdges = this.edges.filter(edge => !(edge.source === sourceNodeId && edge.type === 'normal'));

    // 새 엣지 생성
    const newEdge = generateNormalEdge(sourceNodeId, targetNodeId);

    // reconstruct가 불필요한 노드/엣지를 정리해줌
    this.reconstruct(this.nodes, [newEdge, ...updatedEdges]);

    return { success: true };
  }

  // API 전송 데이터 생성
  toWorkflowCreateDTO(tenantId: string): WorkflowCreateDTO | null {
    if (!this.triggerNode?.data.triggerModel) return null;

    // 1. 그래프 순회하며 액션 수집 (순서 보장)
    const actions: WorkflowActionCreateDTO[] = [];
    const nodeToStepMap = new Map<string, number>(); // 노드 ID -> step 매핑

    traverseGraph(this.nodes, this.edges, node => {
      // action, condition, end 노드 모두 수집
      if ((node.type === 'action' || node.type === 'condition') && node.data.actionModel) {
        const action = {
          ...node.data.actionModel,
          // 빈 값 제외
          actionArguments: node.data.actionModel.actionArguments?.filter(arg => arg.expression.trim() !== '') ?? [],
          // 노드의 현재 position을 서버로 전송
          positionX: Math.round(node.position.x),
          positionY: Math.round(node.position.y),
        };
        nodeToStepMap.set(node.id, actions.length); // 현재 step 저장
        actions.push(action);
      }

      // End 노드도 END_ACTION 타입으로 서버에 전송
      if (node.type === 'end') {
        const endAction = {
          type: 'END_ACTION' as const,
          actionMethod: '',
          actionName: 'End',
          connectionId: '',
          nextStep: -1,
          positionX: Math.round(node.position.x),
          positionY: Math.round(node.position.y),
        };
        nodeToStepMap.set(node.id, actions.length);
        actions.push(endAction);
      }
    });

    // 2. 각 액션의 nextStep, exceptionCatchStep, conditionThenStep 업데이트
    this.nodes.forEach(node => {
      if ((node.type === 'action' || node.type === 'condition') && node.data.actionModel) {
        const currentStep = nodeToStepMap.get(node.id);
        if (currentStep === undefined) return;

        const action = actions[currentStep];

        // Normal Edge (nextStep)
        const normalEdge = this.edges.find(e => e.source === node.id && e.type === 'normal');
        if (normalEdge) {
          const nextStep = nodeToStepMap.get(normalEdge.target);
          action.nextStep = nextStep !== undefined ? nextStep : -1;
        } else {
          action.nextStep = -1;
        }

        // Exception Edge (exceptionCatchStep)
        const exceptionEdge = this.edges.find(e => e.source === node.id && e.type === 'exception');
        if (exceptionEdge) {
          const exceptionStep = nodeToStepMap.get(exceptionEdge.target);
          action.exceptionCatchStep = exceptionStep !== undefined ? exceptionStep : -1;
        } else {
          action.exceptionCatchStep = -1;
        }

        // ConditionThen Edge (conditionThenStep)
        const conditionThenEdge = this.edges.find(e => e.source === node.id && e.type === 'conditionThen');
        if (conditionThenEdge) {
          const conditionThenStep = nodeToStepMap.get(conditionThenEdge.target);
          action.conditionThenStep = conditionThenStep !== undefined ? conditionThenStep : -1;
        } else {
          action.conditionThenStep = -1;
        }
      }
    });

    return {
      actions,
      description: '',
      name: workflowDetailStore.title,
      trigger: this.triggerNode.data.triggerModel,
      type: 'CUSTOM',
      tenantId,
    };
  }
}

const workflowDetailStore = new WorkflowDetailStore();

export default workflowDetailStore;
